import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { Link, useParams } from "react-router-dom";
import apiFetch from "../utils/apiFetch";

const VOLTAGE_COLS = [35, 110, 220, 500];
const RU_COLLATOR = new Intl.Collator("ru", { sensitivity: "base" });

function firstVoltageKv(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  const firstPart = s.split("/")[0]?.trim() ?? "";
  const m = firstPart.match(/\d+/);
  if (!m) return null;
  const kv = Number(m[0]);
  return Number.isFinite(kv) ? kv : null;
}

function pickVoltage(item) {
  return (
    item?.voltage ??
    item?.kv ??
    item?.voltage_kv ??
    item?.voltageLevel ??
    item?.voltage_level ??
    item?.level ??
    null
  );
}

function groupAndSortByVoltage(items) {
  const grouped = Object.fromEntries(VOLTAGE_COLS.map((kv) => [kv, []]));

  (items ?? []).forEach((it) => {
    const kv = firstVoltageKv(pickVoltage(it));
    if (kv && grouped[kv]) grouped[kv].push(it);
  });

  for (const kv of VOLTAGE_COLS) {
    grouped[kv].sort((a, b) =>
      RU_COLLATOR.compare(String(a?.name ?? ""), String(b?.name ?? ""))
    );
  }

  return grouped;
}

function parseDecimalLikeUz(str) {
  if (str === null || str === undefined) return null;
  const s = String(str).replace(",", ".");
  const m = s.match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

function parseMva(str) {
  if (str === null || str === undefined) return null;
  const s = String(str).toLowerCase().replace(",", ".").replace(/\s+/g, "");

  const mult = s.match(/(\d+(\.\d+)?)[xх*](\d+(\.\d+)?)/);
  if (mult) {
    const a = Number(mult[1]);
    const b = Number(mult[3]);
    const res = a * b;
    return Number.isFinite(res) ? res : null;
  }

  if (s.includes("+")) {
    const parts = s.split("+").map((p) => Number(p));
    const ok = parts.every((n) => Number.isFinite(n));
    if (ok) return parts.reduce((acc, n) => acc + n, 0);
  }

  const nums = s.match(/\d+(\.\d+)?/g)?.map(Number) ?? [];
  if (!nums.length) return null;
  const sum = nums.reduce((acc, n) => (Number.isFinite(n) ? acc + n : acc), 0);
  return Number.isFinite(sum) ? sum : null;
}

function parseYear(str) {
  const n = parseInt(String(str ?? ""), 10);
  if (!Number.isFinite(n)) return null;
  if (n < 1900 || n > 2100) return null;
  return n;
}

function fmtNumber(n, maxFrac = 1) {
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: maxFrac }).format(n);
}

const MetricCard = ({ label, value, tone = "blue" }) => (
  <div className={`filial-kpi filial-kpi--${tone}`}>
    <span className="filial-kpi__label">{label}</span>
    <strong className="filial-kpi__value">{value}</strong>
  </div>
);

const VoltageBlock = ({ title, grouped, linkBase, typeLabel }) => {
  const total = VOLTAGE_COLS.reduce((acc, kv) => acc + (grouped?.[kv]?.length ?? 0), 0);

  return (
    <section className="filial-card">
      <div className="filial-card__header">
        <div>
          <p className="filial-card__eyebrow">{typeLabel}</p>
          <h2 className="filial-card__title">{title}</h2>
        </div>
        <span className="filial-card__count">{total}</span>
      </div>

      <div className="filial-voltage">
        {VOLTAGE_COLS.map((kv) => {
          const list = grouped?.[kv] ?? [];
          return (
            <div className="filial-voltage__col" key={kv}>
              <div className="filial-voltage__head">
                <span>{kv} кВ</span>
                <b>{list.length}</b>
              </div>

              <ul className="filial-voltage__list">
                {list.length ? (
                  list.map((it) => {
                    const key = it.id ?? it.slug ?? it.name;
                    const name = it.name || "Номсиз объект";

                    return (
                      <li className="filial-voltage__item" key={key}>
                        {it.slug ? (
                          <Link className="filial-voltage__link" to={`${linkBase}/${it.slug}`}>
                            {name}
                          </Link>
                        ) : (
                          <span className="filial-voltage__link filial-voltage__link--disabled">
                            {name}
                          </span>
                        )}
                      </li>
                    );
                  })
                ) : (
                  <li className="filial-voltage__empty">Маълумот йўқ</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const FilialSummary = ({ stats }) => {
  const years =
    stats.yearMin && stats.yearMax
      ? `${stats.yearMin}-${stats.yearMax}`
      : stats.yearMin || stats.yearMax || "—";

  return (
    <section className="filial-summary">
      <div className="filial-summary__grid">
        <MetricCard label="Подстанциялар" value={stats.substationsCount} />
        <MetricCard label="ПС қуввати" value={`${fmtNumber(stats.totalMva, 1)} МВА`} tone="cyan" />
        <MetricCard label="Тармоқлар" value={stats.networksCount} tone="green" />
        <MetricCard label="Умумий узунлик" value={`${fmtNumber(stats.totalLengthKm, 2)} км`} tone="violet" />
        <MetricCard label="Ўртача линия" value={`${fmtNumber(stats.avgLineKm, 2)} км`} tone="amber" />
        <MetricCard label="Йиллар диапазони" value={years} tone="slate" />
      </div>

      <div className="filial-summary__extra">
        <span>Оптик симли тармоқлар: {stats.networksWithOptic}</span>
        <span>Координатали ПС: {stats.substationsWithCoords}</span>
        <span>
          Энг узун линия:{" "}
          {stats.longestLine ? (
            stats.longestLine.slug ? (
              <Link to={`/indicators/networks/${stats.longestLine.slug}`}>
                {stats.longestLine.name} · {fmtNumber(stats.longestLine.lengthKm, 2)} км
              </Link>
            ) : (
              `${stats.longestLine.name} · ${fmtNumber(stats.longestLine.lengthKm, 2)} км`
            )
          ) : (
            "—"
          )}
        </span>
      </div>
    </section>
  );
};

const Filial = () => {
  const { slug } = useParams();
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch(`/api/city/${encodeURIComponent(slug)}`, { cache: "no-store" });
        if (!res.ok) throw new Error("CITY_LOAD_FAILED");
        const data = await res.json();
        if (!alive) return;
        setCityData(data);
      } catch (e) {
        console.error("Error fetching data:", e);
        if (!alive) return;
        setCityData(null);
        setError("Филиал маълумотлари юкланмади.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [slug, retryKey]);

  const substations = useMemo(() => cityData?.substations ?? [], [cityData]);
  const networks = useMemo(() => cityData?.networks ?? [], [cityData]);

  const substationsGrouped = useMemo(() => groupAndSortByVoltage(substations), [substations]);
  const networksGrouped = useMemo(() => groupAndSortByVoltage(networks), [networks]);

  const stats = useMemo(() => {
    const networksCount = networks.length;
    const substationsCount = substations.length;

    let lengthCount = 0;
    let totalLengthKm = 0;
    let longestLine = null;
    let networksWithOptic = 0;
    const years = [];

    networks.forEach((n) => {
      if (n.optical_wire_type) networksWithOptic += 1;

      const l = parseDecimalLikeUz(n.length);
      if (Number.isFinite(l)) {
        totalLengthKm += l;
        lengthCount += 1;
        if (!longestLine || l > longestLine.lengthKm) {
          longestLine = { name: n.name, slug: n.slug, lengthKm: l };
        }
      }

      const year = parseYear(n.start_year);
      if (Number.isFinite(year)) years.push(year);
    });

    const avgLineKm = lengthCount ? totalLengthKm / lengthCount : 0;

    let totalMva = 0;
    let substationsWithCoords = 0;

    substations.forEach((s) => {
      if (s.latitude && s.longitude) substationsWithCoords += 1;

      const mva = parseMva(s.power_mva);
      if (Number.isFinite(mva)) totalMva += mva;

      const year = parseYear(s.start_year);
      if (Number.isFinite(year)) years.push(year);
    });

    const yearMin = years.length ? Math.min(...years) : null;
    const yearMax = years.length ? Math.max(...years) : null;

    return {
      networksCount,
      substationsCount,
      totalLengthKm,
      avgLineKm,
      totalMva,
      networksWithOptic,
      substationsWithCoords,
      yearMin,
      yearMax,
      longestLine,
    };
  }, [networks, substations]);

  return (
    <Layout>
      <div className="filial">
        <div className="container">
          <nav className="filial-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Асосий</Link>
            <span>/</span>
            <span>Филиал</span>
          </nav>

          <section className="filial-hero">
            <div className="filial-hero__copy">
              <p className="filial-hero__eyebrow">Филиал кўрсаткичлари</p>
              <h1 className="filial__title">{cityData?.name || "Филиал"}</h1>
              <p className="filial-hero__meta">
                Подстанция ва тармоқ объектлари кучланиш даражалари кесимида.
              </p>
            </div>
            <div className="filial-hero__status">
              <span className={`filial-status ${error ? "is-error" : "is-ready"}`}>
                {loading ? "Юкланмоқда" : error ? "Хато" : "Тайёр"}
              </span>
            </div>
          </section>

          {loading && (
            <div className="filial-state">
              <div className="filial-state__spinner" />
              <span>Маълумотлар юкланмоқда...</span>
            </div>
          )}

          {!loading && error && (
            <div className="filial-state filial-state--error">
              <strong>{error}</strong>
              <button type="button" onClick={() => setRetryKey((v) => v + 1)}>
                Қайта уриниш
              </button>
            </div>
          )}

          {!loading && !error && cityData && (
            <>
              <FilialSummary stats={stats} />

              <div className="filial-grid">
                <VoltageBlock
                  title="Подстанциялар"
                  typeLabel="Подстанциялар"
                  grouped={substationsGrouped}
                  linkBase="/indicators/substations"
                />

                <VoltageBlock
                  title="Тармоқлар"
                  typeLabel="Тармоқлар"
                  grouped={networksGrouped}
                  linkBase="/indicators/networks"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Filial;
